import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Loader2, Check, Save, Search, MapPin, Globe, Tag, Clock, 
  CheckCircle, XCircle, Sparkles, Bot, Mail, BarChart3, 
  MessageSquare, Image, Settings, AlertCircle, HelpCircle, BookOpen,
  ToggleLeft, ToggleRight
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// SEO Tab
export const SEOTab = () => {
  const [settings, setSettings] = useState({
    seo_title: '',
    seo_description: '',
    seo_keywords: '',
    og_image: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/admin/settings`);
      setSettings({
        seo_title: response.data.seo_title || '',
        seo_description: response.data.seo_description || '',
        seo_keywords: response.data.seo_keywords || '',
        og_image: response.data.og_image || '',
      });
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    setSaved(false);
    try {
      await axios.put(`${API}/admin/settings`, settings);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (error) {
      console.error('Error saving settings:', error);
      alert('Chyba pri ukladaní');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#00BFFF]" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <Search className="w-6 h-6 text-[#00BFFF]" />
            SEO Nastavenia
          </h1>
          <p className="text-zinc-400 text-sm mt-1">Optimalizácia pre vyhľadávače</p>
        </div>
        <button
          onClick={saveSettings}
          disabled={saving}
          className="flex items-center gap-2 px-6 py-2.5 bg-[#00BFFF] text-[#05050A] font-semibold rounded-lg hover:bg-[#33CCFF] transition-colors disabled:opacity-50"
        >
          {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : saved ? <Check className="w-5 h-5" /> : <Save className="w-5 h-5" />}
          {saved ? 'Uložené' : 'Uložiť'}
        </button>
      </div>

      {/* Meta Tags */}
      <div className="bg-white/5 border border-white/10 rounded-xl p-6">
        <h3 className="text-lg font-semibold mb-6 flex items-center gap-2">
          <Tag className="w-5 h-5 text-[#00BFFF]" />
          Meta tagy
        </h3>

        <div className="space-y-5">
          <div>
            <label className="block text-sm font-medium text-zinc-400 mb-2">
              Titulok stránky (Title)
            </label>
            <input
              type="text"
              value={settings.seo_title}
              onChange={(e) => setSettings({ ...settings, seo_title: e.target.value })}
              className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#00BFFF]"
              placeholder="JasneSvetla.sk | Renovácia svetlometov"
            />
            <p className="text-xs text-zinc-500 mt-1">
              {settings.seo_title.length}/60 znakov (odporúčané max 60)
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-zinc-400 mb-2">
              Popis stránky (Meta Description)
            </label>
            <textarea
              value={settings.seo_description}
              onChange={(e) => setSettings({ ...settings, seo_description: e.target.value })}
              className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#00BFFF] min-h-[100px] resize-none"
              placeholder="Profesionálna renovácia svetlometov..."
            />
            <p className="text-xs text-zinc-500 mt-1">
              {settings.seo_description.length}/160 znakov (odporúčané max 160)
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-zinc-400 mb-2">
              Kľúčové slová (Keywords)
            </label>
            <textarea
              value={settings.seo_keywords}
              onChange={(e) => setSettings({ ...settings, seo_keywords: e.target.value })}
              className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#00BFFF] min-h-[80px] resize-none"
              placeholder="renovácia svetlometov, LED osvetlenie, hviezdny strop..."
            />
            <p className="text-xs text-zinc-500 mt-1">
              Oddeľte čiarkou
            </p>
          </div>
        </div>
      </div>

      {/* Open Graph */}
      <div className="bg-white/5 border border-white/10 rounded-xl p-6">
        <h3 className="text-lg font-semibold mb-6 flex items-center gap-2">
          <Globe className="w-5 h-5 text-[#00BFFF]" />
          Open Graph (Sociálne siete)
        </h3>

        <div>
          <label className="block text-sm font-medium text-zinc-400 mb-2">
            OG Obrázok (URL)
          </label>
          <input
            type="text"
            value={settings.og_image}
            onChange={(e) => setSettings({ ...settings, og_image: e.target.value })}
            className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#00BFFF]"
            placeholder="https://..."
          />
          <p className="text-xs text-zinc-500 mt-1">
            Obrázok zobrazený pri zdieľaní na Facebook, LinkedIn atď. (1200x630px)
          </p>
        </div>
      </div>

      {/* Preview */}
      <div className="bg-white/5 border border-white/10 rounded-xl p-6">
        <h3 className="text-lg font-semibold mb-4">Náhľad vo vyhľadávači</h3>
        <div className="bg-white rounded-lg p-4 text-black">
          <p className="text-blue-600 text-lg hover:underline cursor-pointer truncate">
            {settings.seo_title || 'Titulok stránky'}
          </p>
          <p className="text-green-700 text-sm">jasnesvetla.sk</p>
          <p className="text-gray-600 text-sm mt-1 line-clamp-2">
            {settings.seo_description || 'Popis stránky sa zobrazí tu...'}
          </p>
        </div>
      </div>

      {/* Tips */}
      <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-6">
        <h3 className="text-lg font-semibold mb-3 flex items-center gap-2 text-blue-400">
          <Sparkles className="w-5 h-5" />
          SEO Tipy
        </h3>
        <ul className="space-y-2 text-sm text-zinc-400">
          <li className="flex items-start gap-2">
            <Check className="w-4 h-4 text-blue-400 mt-0.5" />
            Použite hlavné kľúčové slovo v titulku
          </li>
          <li className="flex items-start gap-2">
            <Check className="w-4 h-4 text-blue-400 mt-0.5" />
            Popis by mal obsahovať výzvu k akcii
          </li>
          <li className="flex items-start gap-2">
            <Check className="w-4 h-4 text-blue-400 mt-0.5" />
            Zahrňte lokalitu (Bernolákovo, Bratislava)
          </li>
        </ul>
      </div>
    </div>
  );
};

// GEO Tab
export const GEOTab = () => {
  const [settings, setSettings] = useState({
    business_name: '',
    business_address: '',
    business_city: '',
    business_zip: '',
    business_country: '',
    business_lat: 48.1967,
    business_lng: 17.3000,
    business_phone: '',
    business_email: '',
    hours_weekday: '',
    hours_saturday: '',
    hours_sunday: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/admin/settings`);
      setSettings({
        business_name: response.data.business_name || '',
        business_address: response.data.business_address || '',
        business_city: response.data.business_city || '',
        business_zip: response.data.business_zip || '',
        business_country: response.data.business_country || '',
        business_lat: response.data.business_lat || 48.1967,
        business_lng: response.data.business_lng || 17.3000,
        business_phone: response.data.business_phone || '',
        business_email: response.data.business_email || '',
        hours_weekday: response.data.hours_weekday || '',
        hours_saturday: response.data.hours_saturday || '',
        hours_sunday: response.data.hours_sunday || '',
      });
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    setSaved(false);
    try {
      await axios.put(`${API}/admin/settings`, settings);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (error) {
      console.error('Error saving settings:', error);
      alert('Chyba pri ukladaní');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#00BFFF]" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <MapPin className="w-6 h-6 text-[#00BFFF]" />
            GEO Nastavenia
          </h1>
          <p className="text-zinc-400 text-sm mt-1">Lokálne SEO a Google Business</p>
        </div>
        <button
          onClick={saveSettings}
          disabled={saving}
          className="flex items-center gap-2 px-6 py-2.5 bg-[#00BFFF] text-[#05050A] font-semibold rounded-lg hover:bg-[#33CCFF] transition-colors disabled:opacity-50"
        >
          {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : saved ? <Check className="w-5 h-5" /> : <Save className="w-5 h-5" />}
          {saved ? 'Uložené' : 'Uložiť'}
        </button>
      </div>

      {/* Business Info */}
      <div className="bg-white/5 border border-white/10 rounded-xl p-6">
        <h3 className="text-lg font-semibold mb-6">Informácie o firme</h3>

        <div className="grid md:grid-cols-2 gap-5">
          <div>
            <label className="block text-sm font-medium text-zinc-400 mb-2">Názov firmy</label>
            <input
              type="text"
              value={settings.business_name}
              onChange={(e) => setSettings({ ...settings, business_name: e.target.value })}
              className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#00BFFF]"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-zinc-400 mb-2">Telefón</label>
            <input
              type="text"
              value={settings.business_phone}
              onChange={(e) => setSettings({ ...settings, business_phone: e.target.value })}
              className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#00BFFF]"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-zinc-400 mb-2">Email</label>
            <input
              type="email"
              value={settings.business_email}
              onChange={(e) => setSettings({ ...settings, business_email: e.target.value })}
              className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#00BFFF]"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-zinc-400 mb-2">Ulica</label>
            <input
              type="text"
              value={settings.business_address}
              onChange={(e) => setSettings({ ...settings, business_address: e.target.value })}
              className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#00BFFF]"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-zinc-400 mb-2">Mesto</label>
            <input
              type="text"
              value={settings.business_city}
              onChange={(e) => setSettings({ ...settings, business_city: e.target.value })}
              className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#00BFFF]"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-zinc-400 mb-2">PSČ</label>
            <input
              type="text"
              value={settings.business_zip}
              onChange={(e) => setSettings({ ...settings, business_zip: e.target.value })}
              className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#00BFFF]"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-zinc-400 mb-2">Krajina</label>
            <input
              type="text"
              value={settings.business_country}
              onChange={(e) => setSettings({ ...settings, business_country: e.target.value })}
              className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#00BFFF]"
            />
          </div>
        </div>
      </div>

      {/* GPS Coordinates */}
      <div className="bg-white/5 border border-white/10 rounded-xl p-6">
        <h3 className="text-lg font-semibold mb-6">GPS Súradnice</h3>
        <div className="grid md:grid-cols-2 gap-5">
          <div>
            <label className="block text-sm font-medium text-zinc-400 mb-2">Zemepisná šírka (Latitude)</label>
            <input
              type="number"
              step="0.0001"
              value={settings.business_lat}
              onChange={(e) => setSettings({ ...settings, business_lat: parseFloat(e.target.value) })}
              className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#00BFFF]"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-zinc-400 mb-2">Zemepisná dĺžka (Longitude)</label>
            <input
              type="number"
              step="0.0001"
              value={settings.business_lng}
              onChange={(e) => setSettings({ ...settings, business_lng: parseFloat(e.target.value) })}
              className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#00BFFF]"
            />
          </div>
        </div>
        <p className="text-xs text-zinc-500 mt-2">
          Nájdite súradnice na Google Maps kliknutím pravým tlačidlom na vašu polohu
        </p>
      </div>

      {/* Opening Hours */}
      <div className="bg-white/5 border border-white/10 rounded-xl p-6">
        <h3 className="text-lg font-semibold mb-6 flex items-center gap-2">
          <Clock className="w-5 h-5 text-[#00BFFF]" />
          Otváracie hodiny
        </h3>
        <div className="grid md:grid-cols-3 gap-5">
          <div>
            <label className="block text-sm font-medium text-zinc-400 mb-2">Pondelok - Piatok</label>
            <input
              type="text"
              value={settings.hours_weekday}
              onChange={(e) => setSettings({ ...settings, hours_weekday: e.target.value })}
              className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#00BFFF]"
              placeholder="08:00-17:00"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-zinc-400 mb-2">Sobota</label>
            <input
              type="text"
              value={settings.hours_saturday}
              onChange={(e) => setSettings({ ...settings, hours_saturday: e.target.value })}
              className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#00BFFF]"
              placeholder="08:00-13:00"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-zinc-400 mb-2">Nedeľa</label>
            <input
              type="text"
              value={settings.hours_sunday}
              onChange={(e) => setSettings({ ...settings, hours_sunday: e.target.value })}
              className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#00BFFF]"
              placeholder="Zatvorené"
            />
          </div>
        </div>
      </div>

      {/* Schema Markup Info */}
      <div className="bg-green-500/10 border border-green-500/20 rounded-xl p-6">
        <h3 className="text-lg font-semibold mb-3 flex items-center gap-2 text-green-400">
          <CheckCircle className="w-5 h-5" />
          Schema Markup
        </h3>
        <p className="text-sm text-zinc-400">
          Tieto údaje sa automaticky použijú pre vytvorenie JSON-LD štruktúrovaných dát, 
          ktoré pomáhajú Google lepšie pochopiť vašu firmu a zobraziť ju vo výsledkoch vyhľadávania.
        </p>
      </div>
    </div>
  );
};

// AI SEO Dashboard Tab
export const AISEOTab = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStatus = async () => {
      try {
        const response = await axios.get(`${API}/ai-seo/status`, { withCredentials: true });
        setStatus(response.data);
      } catch (error) {
        console.error('Error fetching AI SEO status:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchStatus();
  }, []);

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-[#00BFFF]" /></div>;

  const checkLabels = {
    schema_org: { label: 'Schema.org (LocalBusiness)', desc: 'Štruktúrované dáta pre Google a AI' },
    sitemap_xml: { label: 'Sitemap.xml', desc: 'Mapa stránky pre crawlery' },
    robots_txt: { label: 'Robots.txt', desc: 'Povolenie prístupu pre AI boty' },
    llms_txt: { label: 'LLMs.txt', desc: 'Štruktúrované info pre AI vyhľadávače' },
    faq_schema: { label: 'FAQ Schema', desc: 'Rich snippety pre FAQ v Google' },
    seo_title: { label: 'SEO Titulok', desc: 'Meta title stránky' },
    seo_description: { label: 'SEO Popis', desc: 'Meta description stránky' },
    business_info: { label: 'Firemné údaje', desc: 'Názov, adresa, telefón' },
    blog_10_plus: { label: '10+ Blog článkov', desc: 'Dostatočný obsah pre indexovanie' },
    faq_5_plus: { label: '5+ FAQ otázok', desc: 'Dostatočný počet FAQ' },
  };

  const scoreColor = status?.score >= 80 ? 'text-green-400' : status?.score >= 50 ? 'text-yellow-400' : 'text-red-400';
  const scoreBg = status?.score >= 80 ? 'bg-green-500/20 border-green-500/30' : status?.score >= 50 ? 'bg-yellow-500/20 border-yellow-500/30' : 'bg-red-500/20 border-red-500/30';

  return (
    <div data-testid="aiseo-tab" className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold flex items-center gap-2">
          <Globe className="w-6 h-6 text-[#00BFFF]" />
          AI SEO Dashboard
        </h1>
        <p className="text-zinc-400 text-sm mt-1">Optimalizácia pre AI vyhľadávače (ChatGPT, Perplexity, Google AI)</p>
      </div>

      {/* Score */}
      <div className={`p-6 border rounded-xl ${scoreBg}`}>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-zinc-400 mb-1">AI SEO Skóre</p>
            <p className={`text-5xl font-bold ${scoreColor}`}>{status?.score}%</p>
          </div>
          <div className="text-right text-sm text-zinc-400">
            <p>{status?.blog_count} blog článkov</p>
            <p>{status?.faq_count} FAQ otázok</p>
          </div>
        </div>
      </div>

      {/* Checks */}
      <div className="bg-white/5 border border-white/10 rounded-xl overflow-hidden">
        <div className="px-6 py-4 border-b border-white/10">
          <h3 className="text-sm font-bold uppercase tracking-wider text-[#00BFFF]">Kontrolný zoznam</h3>
        </div>
        <div className="divide-y divide-white/5">
          {status?.checks && Object.entries(status.checks).map(([key, passed]) => {
            const info = checkLabels[key] || { label: key, desc: '' };
            return (
              <div key={key} data-testid={`aiseo-check-${key}`} className="flex items-center justify-between px-6 py-3.5 hover:bg-white/[0.02] transition-colors">
                <div className="flex items-center gap-3">
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center ${passed ? 'bg-green-500/20' : 'bg-red-500/20'}`}>
                    {passed ? <Check size={14} className="text-green-400" /> : <XCircle size={14} className="text-red-400" />}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-white">{info.label}</p>
                    <p className="text-xs text-zinc-500">{info.desc}</p>
                  </div>
                </div>
                <span className={`text-xs font-medium px-2.5 py-1 rounded ${passed ? 'bg-green-500/10 text-green-400' : 'bg-red-500/10 text-red-400'}`}>
                  {passed ? 'OK' : 'Chýba'}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Endpoints */}
      <div className="bg-white/5 border border-white/10 rounded-xl overflow-hidden">
        <div className="px-6 py-4 border-b border-white/10">
          <h3 className="text-sm font-bold uppercase tracking-wider text-[#00BFFF]">Aktívne endpointy</h3>
        </div>
        <div className="p-6 space-y-3">
          {status?.endpoints && Object.entries(status.endpoints).map(([key, path]) => (
            <div key={key} className="flex items-center justify-between p-3 bg-white/[0.03] border border-white/5 rounded-lg">
              <div className="flex items-center gap-3">
                <CheckCircle size={16} className="text-green-400" />
                <span className="text-sm font-medium text-zinc-300">{key.replace(/_/g, ' ')}</span>
              </div>
              <code className="text-xs text-[#00BFFF] bg-[#00BFFF]/10 px-2 py-1 rounded font-mono">{path}</code>
            </div>
          ))}
        </div>
      </div>

      {/* RAG Chatbot Info */}
      <div className="bg-purple-500/5 border border-purple-500/20 rounded-xl p-6">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-xl bg-purple-500/20 flex items-center justify-center flex-shrink-0">
            <Bot className="w-6 h-6 text-purple-400" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-white">RAG Chatbot</h3>
            <p className="text-sm text-zinc-400 mt-1">
              AI chatbot teraz automaticky vyhľadáva relevantné informácie z FAQ a blog článkov pred tým, ako odpovie zákazníkovi. 
              Tým poskytuje presnejšie a relevantnejšie odpovede.
            </p>
            <div className="flex gap-4 mt-3 text-xs text-zinc-500">
              <span className="flex items-center gap-1"><HelpCircle size={12} /> {status?.faq_count} FAQ v znalostnej báze</span>
              <span className="flex items-center gap-1"><BookOpen size={12} /> {status?.blog_count} článkov v znalostnej báze</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// AI Tab
export const AITab = () => {
  const [settings, setSettings] = useState({
    ai_chatbot_enabled: false,
    ai_auto_reply_enabled: false,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/admin/settings`);
      setSettings({
        ai_chatbot_enabled: response.data.ai_chatbot_enabled || false,
        ai_auto_reply_enabled: response.data.ai_auto_reply_enabled || false,
      });
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleSetting = async (key) => {
    setSaving(true);
    try {
      const newValue = !settings[key];
      await axios.put(`${API}/admin/settings`, { [key]: newValue });
      setSettings({ ...settings, [key]: newValue });
    } catch (error) {
      console.error('Error updating settings:', error);
      alert('Chyba pri ukladaní');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#00BFFF]" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold flex items-center gap-2">
          <Bot className="w-6 h-6 text-[#00BFFF]" />
          AI Nastavenia
        </h1>
        <p className="text-zinc-400 text-sm mt-1">Inteligentné funkcie pre vašu stránku</p>
      </div>

      {/* AI Chatbot */}
      <div className="bg-white/5 border border-white/10 rounded-xl p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-purple-500/20 flex items-center justify-center">
              <Bot className="w-6 h-6 text-purple-400" />
            </div>
            <div>
              <h3 className="text-lg font-semibold">AI Chatbot</h3>
              <p className="text-sm text-zinc-400 mt-1">
                Automatický asistent, ktorý odpovedá na otázky zákazníkov 24/7
              </p>
            </div>
          </div>
          <button
            onClick={() => toggleSetting('ai_chatbot_enabled')}
            disabled={saving}
            className={`relative w-16 h-8 rounded-full transition-colors ${
              settings.ai_chatbot_enabled ? 'bg-purple-500' : 'bg-zinc-600'
            } ${saving ? 'opacity-50' : ''}`}
          >
            <div className={`absolute top-1 w-6 h-6 bg-white rounded-full transition-all shadow-lg ${
              settings.ai_chatbot_enabled ? 'left-9' : 'left-1'
            }`} />
          </button>
        </div>

        {settings.ai_chatbot_enabled && (
          <div className="mt-6 p-4 bg-purple-500/10 border border-purple-500/20 rounded-lg">
            <p className="text-sm text-purple-300">
              <Sparkles className="w-4 h-4 inline mr-1" />
              Chatbot je aktívny! Zákazníci uvidia ikonu chatu na stránke.
            </p>
          </div>
        )}

        {!settings.ai_chatbot_enabled && (
          <div className="mt-6 p-4 bg-zinc-500/10 border border-zinc-500/20 rounded-lg">
            <p className="text-sm text-zinc-400">
              Pre aktiváciu AI chatbota je potrebný API kľúč. Kontaktujte podporu pre nastavenie.
            </p>
          </div>
        )}
      </div>

      {/* Auto Reply */}
      <div className="bg-white/5 border border-white/10 rounded-xl p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-blue-500/20 flex items-center justify-center">
              <Mail className="w-6 h-6 text-blue-400" />
            </div>
            <div>
              <h3 className="text-lg font-semibold">AI Auto-odpoveď</h3>
              <p className="text-sm text-zinc-400 mt-1">
                Automatické odpovede na kontaktné formuláre pomocou AI
              </p>
            </div>
          </div>
          <button
            onClick={() => toggleSetting('ai_auto_reply_enabled')}
            disabled={saving}
            className={`relative w-16 h-8 rounded-full transition-colors ${
              settings.ai_auto_reply_enabled ? 'bg-blue-500' : 'bg-zinc-600'
            } ${saving ? 'opacity-50' : ''}`}
          >
            <div className={`absolute top-1 w-6 h-6 bg-white rounded-full transition-all shadow-lg ${
              settings.ai_auto_reply_enabled ? 'left-9' : 'left-1'
            }`} />
          </button>
        </div>

        {settings.ai_auto_reply_enabled && (
          <div className="mt-6 p-4 bg-blue-500/10 border border-blue-500/20 rounded-lg">
            <p className="text-sm text-blue-300">
              <Sparkles className="w-4 h-4 inline mr-1" />
              AI bude automaticky odpovedať na nové správy z kontaktného formulára.
            </p>
          </div>
        )}
      </div>

      {/* AI Features Coming */}
      <div className="bg-white/5 border border-white/10 rounded-xl p-6">
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-[#00BFFF]" />
          Pripravované AI funkcie
        </h3>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="p-4 bg-white/5 rounded-lg border border-white/10">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-8 h-8 rounded-lg bg-green-500/20 flex items-center justify-center">
                <Search className="w-4 h-4 text-green-400" />
              </div>
              <span className="font-medium">AI SEO Asistent</span>
            </div>
            <p className="text-sm text-zinc-500">
              Automatické generovanie SEO textov a meta popisov
            </p>
          </div>
          <div className="p-4 bg-white/5 rounded-lg border border-white/10">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-8 h-8 rounded-lg bg-orange-500/20 flex items-center justify-center">
                <Image className="w-4 h-4 text-orange-400" />
              </div>
              <span className="font-medium">AI Generátor obrázkov</span>
            </div>
            <p className="text-sm text-zinc-500">
              Vytvorenie marketingových vizuálov pomocou AI
            </p>
          </div>
          <div className="p-4 bg-white/5 rounded-lg border border-white/10">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-8 h-8 rounded-lg bg-pink-500/20 flex items-center justify-center">
                <MessageSquare className="w-4 h-4 text-pink-400" />
              </div>
              <span className="font-medium">AI FAQ Generátor</span>
            </div>
            <p className="text-sm text-zinc-500">
              Automatické vytváranie FAQ na základe otázok
            </p>
          </div>
          <div className="p-4 bg-white/5 rounded-lg border border-white/10">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-8 h-8 rounded-lg bg-cyan-500/20 flex items-center justify-center">
                <BarChart3 className="w-4 h-4 text-cyan-400" />
              </div>
              <span className="font-medium">AI Analytika</span>
            </div>
            <p className="text-sm text-zinc-500">
              Inteligentná analýza návštevnosti a konverzií
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Settings Tab
export const SettingsTab = () => {
  const [settings, setSettings] = useState({ 
    booking_enabled: true,
    light_effect_enabled: true,
    light_effect_speed: 'normal',
    light_effect_intensity: 'medium'
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/admin/settings`);
      setSettings(response.data);
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleBooking = async () => {
    setSaving(true);
    try {
      const newValue = !settings.booking_enabled;
      await axios.put(`${API}/admin/settings`, { booking_enabled: newValue });
      setSettings({ ...settings, booking_enabled: newValue });
    } catch (error) {
      console.error('Error updating settings:', error);
      alert('Chyba pri ukladaní nastavení');
    } finally {
      setSaving(false);
    }
  };

  const toggleLightEffect = async () => {
    setSaving(true);
    try {
      const newValue = !settings.light_effect_enabled;
      await axios.put(`${API}/admin/settings`, { light_effect_enabled: newValue });
      setSettings({ ...settings, light_effect_enabled: newValue });
    } catch (error) {
      console.error('Error updating settings:', error);
      alert('Chyba pri ukladaní nastavení');
    } finally {
      setSaving(false);
    }
  };

  const updateLightSpeed = async (speed) => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/settings`, { light_effect_speed: speed });
      setSettings({ ...settings, light_effect_speed: speed });
    } catch (error) {
      console.error('Error updating settings:', error);
      alert('Chyba pri ukladaní nastavení');
    } finally {
      setSaving(false);
    }
  };

  const updateLightIntensity = async (intensity) => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/settings`, { light_effect_intensity: intensity });
      setSettings({ ...settings, light_effect_intensity: intensity });
    } catch (error) {
      console.error('Error updating settings:', error);
      alert('Chyba pri ukladaní nastavení');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#00BFFF]" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Nastavenia</h1>
        <p className="text-zinc-400 text-sm mt-1">Spravujte nastavenia vašej stránky</p>
      </div>

      <div className="bg-white/5 border border-white/10 rounded-xl p-6">
        <h3 className="text-lg font-semibold mb-6">Rezervačný systém</h3>
        
        <div className="flex items-center justify-between p-4 bg-white/5 rounded-lg">
          <div>
            <p className="font-medium">Online rezervácie</p>
            <p className="text-sm text-zinc-400 mt-1">
              {settings.booking_enabled 
                ? 'Zákazníci môžu vytvárať rezervácie cez webstránku'
                : 'Rezervačný systém je vypnutý, zákazníci musia volať'}
            </p>
          </div>
          <button
            onClick={toggleBooking}
            disabled={saving}
            data-testid="toggle-booking"
            className={`relative w-16 h-8 rounded-full transition-colors ${
              settings.booking_enabled ? 'bg-[#00BFFF]' : 'bg-zinc-600'
            } ${saving ? 'opacity-50' : ''}`}
          >
            <div className={`absolute top-1 w-6 h-6 bg-white rounded-full transition-all shadow-lg ${
              settings.booking_enabled ? 'left-9' : 'left-1'
            }`} />
          </button>
        </div>

        <div className={`mt-4 p-4 rounded-lg ${
          settings.booking_enabled 
            ? 'bg-green-500/10 border border-green-500/20' 
            : 'bg-yellow-500/10 border border-yellow-500/20'
        }`}>
          <div className="flex items-center gap-2">
            {settings.booking_enabled ? (
              <>
                <CheckCircle className="w-5 h-5 text-green-400" />
                <span className="text-green-400 font-medium">Rezervácie sú aktívne</span>
              </>
            ) : (
              <>
                <AlertCircle className="w-5 h-5 text-yellow-400" />
                <span className="text-yellow-400 font-medium">Rezervácie sú vypnuté</span>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Light Effect Settings */}
      <div className="bg-white/5 border border-white/10 rounded-xl p-6">
        <h3 className="text-lg font-semibold mb-6 flex items-center gap-2">
          <span className="w-3 h-3 rounded-full bg-[#00BFFF] animate-pulse" />
          Blikajúce svetlá
        </h3>
        
        {/* Enable/Disable */}
        <div className="flex items-center justify-between p-4 bg-white/5 rounded-lg mb-4">
          <div>
            <p className="font-medium">Svetelný efekt</p>
            <p className="text-sm text-zinc-400 mt-1">
              Animované blikajúce svetlá na úvodnej stránke
            </p>
          </div>
          <button
            onClick={toggleLightEffect}
            disabled={saving}
            data-testid="toggle-light-effect"
            className={`relative w-16 h-8 rounded-full transition-colors ${
              settings.light_effect_enabled ? 'bg-[#00BFFF]' : 'bg-zinc-600'
            } ${saving ? 'opacity-50' : ''}`}
          >
            <div className={`absolute top-1 w-6 h-6 bg-white rounded-full transition-all shadow-lg ${
              settings.light_effect_enabled ? 'left-9' : 'left-1'
            }`} />
          </button>
        </div>

        {settings.light_effect_enabled && (
          <div className="space-y-4 mt-4">
            {/* Speed */}
            <div className="p-4 bg-white/5 rounded-lg">
              <p className="font-medium mb-3">Rýchlosť blikania</p>
              <div className="flex gap-2">
                {[
                  { value: 'slow', label: 'Pomalé', desc: '12s' },
                  { value: 'normal', label: 'Normálne', desc: '8s' },
                  { value: 'fast', label: 'Rýchle', desc: '4s' },
                ].map((option) => (
                  <button
                    key={option.value}
                    onClick={() => updateLightSpeed(option.value)}
                    disabled={saving}
                    className={`flex-1 py-3 px-4 rounded-lg text-sm font-medium transition-all ${
                      settings.light_effect_speed === option.value
                        ? 'bg-[#00BFFF] text-[#05050A]'
                        : 'bg-white/10 text-zinc-400 hover:bg-white/20'
                    } ${saving ? 'opacity-50' : ''}`}
                  >
                    <div>{option.label}</div>
                    <div className="text-xs opacity-70 mt-1">{option.desc}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Intensity */}
            <div className="p-4 bg-white/5 rounded-lg">
              <p className="font-medium mb-3">Intenzita svetla</p>
              <div className="flex gap-2">
                {[
                  { value: 'low', label: 'Jemné', icon: '○' },
                  { value: 'medium', label: 'Stredné', icon: '◐' },
                  { value: 'high', label: 'Silné', icon: '●' },
                ].map((option) => (
                  <button
                    key={option.value}
                    onClick={() => updateLightIntensity(option.value)}
                    disabled={saving}
                    className={`flex-1 py-3 px-4 rounded-lg text-sm font-medium transition-all ${
                      settings.light_effect_intensity === option.value
                        ? 'bg-[#00BFFF] text-[#05050A]'
                        : 'bg-white/10 text-zinc-400 hover:bg-white/20'
                    } ${saving ? 'opacity-50' : ''}`}
                  >
                    <div className="text-lg mb-1">{option.icon}</div>
                    <div>{option.label}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Preview */}
            <div className="p-4 bg-black/30 rounded-lg border border-white/10">
              <p className="text-sm text-zinc-400 mb-3">Náhľad efektu:</p>
              <div className="relative h-20 flex items-center justify-center gap-8">
                <div 
                  className={`rounded-full bg-[#00BFFF] ${
                    settings.light_effect_intensity === 'low' ? 'w-4 h-4 blur-md' :
                    settings.light_effect_intensity === 'medium' ? 'w-6 h-6 blur-lg' :
                    'w-8 h-8 blur-xl'
                  }`}
                  style={{
                    animation: `headlightFlicker ${
                      settings.light_effect_speed === 'slow' ? '12s' :
                      settings.light_effect_speed === 'fast' ? '4s' : '8s'
                    } ease-in-out infinite`
                  }}
                />
                <div 
                  className={`rounded-full bg-white ${
                    settings.light_effect_intensity === 'low' ? 'w-3 h-3 blur-sm' :
                    settings.light_effect_intensity === 'medium' ? 'w-4 h-4 blur-md' :
                    'w-6 h-6 blur-lg'
                  }`}
                  style={{
                    animation: `headlightFlicker ${
                      settings.light_effect_speed === 'slow' ? '12s' :
                      settings.light_effect_speed === 'fast' ? '4s' : '8s'
                    } ease-in-out infinite`,
                    animationDelay: '3s'
                  }}
                />
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="bg-white/5 border border-white/10 rounded-xl p-6">
        <h3 className="text-lg font-semibold mb-4">Kontaktné údaje</h3>
        <div className="space-y-3 text-sm">
          <div className="flex items-center justify-between py-2 border-b border-white/5">
            <span className="text-zinc-400">Telefón</span>
            <span>+421 915 264 064</span>
          </div>
          <div className="flex items-center justify-between py-2 border-b border-white/5">
            <span className="text-zinc-400">Email</span>
            <span>m.petor@seznam.cz</span>
          </div>
          <div className="flex items-center justify-between py-2 border-b border-white/5">
            <span className="text-zinc-400">Adresa</span>
            <span>Šípková 5, 900 27 Bernolákovo</span>
          </div>
          <div className="flex items-center justify-between py-2">
            <span className="text-zinc-400">Otváracie hodiny</span>
            <span>Po-Pia 8-17, So 8-13</span>
          </div>
        </div>
      </div>

      <div className="bg-white/5 border border-white/10 rounded-xl p-6">
        <h3 className="text-lg font-semibold mb-4">Budúce funkcie</h3>
        <ul className="space-y-3 text-sm text-zinc-400">
          <li className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-zinc-500" />
            Email notifikácie pri novej rezervácii
          </li>
          <li className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-zinc-500" />
            SMS pripomienky pred termínom
          </li>
          <li className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-zinc-500" />
            E-shop pre predaj príslušenstva
          </li>
        </ul>
      </div>
    </div>
  );
};
